import { useQueries, UseQueryOptions } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/hooks/use-permissions";
import { ChipId, ChipRow, formatChipNames } from "@/lib/filter-chip-label";
import { PermissionCheck } from "@/lib/list-permissions";

/**
 * One source of names for an id filter's chip: a list query, the rows it names, and what its
 * endpoint requires. Build one with `chipLookup` (filter-chip-lookups.tsx), which types `rows`
 * against the query's data and sets how long the chip's copy stays fresh.
 */
export type ChipLookup = {
  query: UseQueryOptions<any, any, any, any>;
  rows: (data: any) => readonly ChipRow[] | undefined;
  permission: PermissionCheck;
};

/**
 * Renders the names behind an id filter's active-filter chip, e.g. "Tags: Test 1, test 2".
 *
 * The chip holds its own lookups -- subscribed queries -- so it updates once the names arrive.
 * DataTableActiveFilters used to label a key without a formatter from whatever the React Query
 * cache held when it rendered, and never looked again: a link opened cold showed the raw id, and its
 * key matching could land on the table's own list query and show another entity's name (ID-7069,
 * ID-7084). A formatter is a plain function and can't use hooks itself, so it returns this element
 * instead; see lookupChipFormatter.
 *
 * Pass the same query options the filter's own control loads where it loads the whole list, so
 * both share one cache entry. Several lookups merge their rows: the assignee chip adds the signed-in
 * user to the account's users, as the Assignee filter offers them.
 *
 * Each lookup's `permission` is what its endpoint requires. Any 403 sends the whole page to
 * /dashboard/unauthorized (the response interceptor in client/index.ts), so without it that lookup
 * never asks, and a list it finds already cached goes unread as well: its ids read "#<id>" rather
 * than throwing the user off a page opened from a shared link.
 */
export function LookupFilterChipValue({
  ids,
  lookups,
}: {
  ids: readonly ChipId[];
  lookups: readonly ChipLookup[];
}) {
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  const allowed = lookups.map((lookup) =>
    Boolean(lookup.permission(permissions)),
  );
  const results = useQueries({
    queries: lookups.map((lookup, index) => ({
      ...lookup.query,
      enabled: allowed[index],
    })),
  });
  // A disabled query still hands back what the cache holds -- a list loaded before a SuperAdmin
  // previews a role without the permission, or before a role loses it -- so it is not read either.
  const rows = lookups.flatMap((lookup, index) => {
    const data = allowed[index] ? results[index]?.data : undefined;
    return data === undefined ? [] : (lookup.rows(data) ?? []);
  });
  return <>{formatChipNames(ids, rows, t("unassigned"))}</>;
}
